if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.touchStartX = 0;
        this.touchStartY = 0;
        this.swipeHandled = false;

        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handlePrevious = this.handlePrevious.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleArrowPointer = this.handleArrowPointer.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        super.connectedCallback?.();

        this.addEventListener(
          'touchstart',
          this.handleTouchStart,
          {
            passive: true,
            capture: true
          }
        );

        this.addEventListener(
          'touchmove',
          this.handleTouchMove,
          {
            passive: true,
            capture: true
          }
        );

        this.addEventListener(
          'touchend',
          this.handleTouchEnd,
          {
            passive: true,
            capture: true
          }
        );

        this.addEventListener(
          'pointerup',
          this.handleArrowPointer,
          true
        );

        /*
         * KEYBOARD NAVIGATION
         */
        document.addEventListener(
          'keydown',
          this.handleKeydown
        );

        const previousButton = this.querySelector(
          '.product-media-modal__arrow--prev'
        );

        const nextButton = this.querySelector(
          '.product-media-modal__arrow--next'
        );

        if (previousButton) {
          previousButton.addEventListener(
            'click',
            this.handlePrevious,
            true
          );
        }

        if (nextButton) {
          nextButton.addEventListener(
            'click',
            this.handleNext,
            true
          );
        }
      }

      disconnectedCallback() {
        this.removeEventListener(
          'touchstart',
          this.handleTouchStart,
          true
        );

        this.removeEventListener(
          'touchmove',
          this.handleTouchMove,
          true
        );

        this.removeEventListener(
          'touchend',
          this.handleTouchEnd,
          true
        );

        this.removeEventListener(
          'pointerup',
          this.handleArrowPointer,
          true
        );

        document.removeEventListener(
          'keydown',
          this.handleKeydown
        );

        const previousButton = this.querySelector(
          '.product-media-modal__arrow--prev'
        );

        const nextButton = this.querySelector(
          '.product-media-modal__arrow--next'
        );

        if (previousButton) {
          previousButton.removeEventListener(
            'click',
            this.handlePrevious,
            true
          );
        }

        if (nextButton) {
          nextButton.removeEventListener(
            'click',
            this.handleNext,
            true
          );
        }
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      /*
       * KEYBOARD NAVIGATION
       */

      handleKeydown(event) {
        /*
         * Only respond when the product modal
         * is actually open.
         */
        if (!this.openedBy) return;

        /*
         * Don't interfere with typing into
         * form fields elsewhere on the page.
         */
        const activeElement = document.activeElement;

        if (
          activeElement &&
          (
            activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.tagName === 'SELECT' ||
            activeElement.isContentEditable
          )
        ) {
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.handlePrevious(event);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.handleNext(event);
        }
      }

      /*
       * Stop the modal background click handler
       * from seeing clicks on our navigation arrows.
       */

      handleArrowPointer(event) {
        const arrow = event.target.closest(
          '.product-media-modal__arrow'
        );

        if (!arrow) return;

        event.stopImmediatePropagation();
      }

      getMediaItems() {
        return Array.from(
          this.querySelectorAll('[data-media-id]')
        );
      }

      getActiveMedia() {
        return this.querySelector(
          '[data-media-id].active'
        );
      }

      goToMedia(index) {
        const mediaItems = this.getMediaItems();

        if (!mediaItems.length) return;

        if (index < 0 || index >= mediaItems.length) {
          return;
        }

        this.showMedia(mediaItems[index]);
      }

      handlePrevious(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const mediaItems = this.getMediaItems();
        const activeMedia = this.getActiveMedia();

        if (!activeMedia) return;

        const currentIndex =
          mediaItems.indexOf(activeMedia);

        this.goToMedia(currentIndex - 1);
      }

      handleNext(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const mediaItems = this.getMediaItems();
        const activeMedia = this.getActiveMedia();

        if (!activeMedia) return;

        const currentIndex =
          mediaItems.indexOf(activeMedia);

        this.goToMedia(currentIndex + 1);
      }

      /*
       * LOAD A MODAL IMAGE ONLY WHEN IT IS NEEDED
       */

      loadModalImage(media) {
        if (!media) return;

        const image = media.querySelector(
          'img[data-lazy-modal-image]'
        );

        if (!image) return;

        if (image.dataset.loaded === 'true') {
          return;
        }

        const srcset = image.dataset.srcset;
        const src = image.dataset.src;
        const sizes = image.dataset.sizes;

        if (srcset) {
          image.setAttribute(
            'srcset',
            srcset
          );
        }

        if (sizes) {
          image.setAttribute(
            'sizes',
            sizes
          );
        }

        if (src) {
          image.setAttribute(
            'src',
            src
          );
        }

        image.dataset.loaded = 'true';
      }

      /*
       * PRELOAD ONLY THE NEIGHBOURING IMAGES
       */

      preloadAdjacentMedia(media) {
        const mediaItems = this.getMediaItems();

        const currentIndex =
          mediaItems.indexOf(media);

        if (currentIndex === -1) return;

        const previousMedia =
          mediaItems[currentIndex - 1];

        const nextMedia =
          mediaItems[currentIndex + 1];

        if (previousMedia) {
          this.loadModalImage(previousMedia);
        }

        if (nextMedia) {
          this.loadModalImage(nextMedia);
        }
      }

      /*
       * MOBILE SWIPE
       */

      handleTouchStart(event) {
        if (
          !event.touches ||
          event.touches.length !== 1
        ) {
          return;
        }

        this.touchStartX =
          event.touches[0].clientX;

        this.touchStartY =
          event.touches[0].clientY;

        this.swipeHandled = false;
      }

      handleTouchMove(event) {
        if (
          !event.touches ||
          event.touches.length !== 1
        ) {
          return;
        }

        const currentX =
          event.touches[0].clientX;

        const currentY =
          event.touches[0].clientY;

        const distanceX =
          Math.abs(
            currentX -
            this.touchStartX
          );

        const distanceY =
          Math.abs(
            currentY -
            this.touchStartY
          );

        if (
          distanceX > distanceY &&
          distanceX > 20
        ) {
          this.swipeHandled = true;
        }
      }

      handleTouchEnd(event) {
        if (!this.swipeHandled) return;

        const touch =
          event.changedTouches &&
          event.changedTouches[0];

        if (!touch) return;

        const distanceX =
          this.touchStartX -
          touch.clientX;

        if (
          Math.abs(distanceX) < 50
        ) {
          this.swipeHandled = false;
          return;
        }

        const mediaItems =
          this.getMediaItems();

        const activeMedia =
          this.getActiveMedia();

        if (!activeMedia) return;

        const currentIndex =
          mediaItems.indexOf(activeMedia);

        if (distanceX > 0) {
          // Swipe LEFT → next image
          this.goToMedia(
            currentIndex + 1
          );
        } else {
          // Swipe RIGHT → previous image
          this.goToMedia(
            currentIndex - 1
          );
        }

        this.swipeHandled = false;
      }

      /*
       * SHOW THE IMAGE THAT WAS CLICKED
       */

      showActiveMedia() {
        const mediaId =
          this.openedBy.getAttribute(
            'data-media-id'
          );

        this.querySelectorAll(
          '[data-media-id]'
        ).forEach((element) => {
          element.classList.toggle(
            'active',
            element.getAttribute(
              'data-media-id'
            ) === mediaId
          );
        });

        const activeMedia =
          this.querySelector(
            `[data-media-id="${mediaId}"]`
          );

        if (!activeMedia) return;

        /*
         * Only load the image being opened.
         */
        this.loadModalImage(
          activeMedia
        );

        const activeMediaTemplate =
          activeMedia.querySelector(
            'template'
          );

        const activeMediaContent =
          activeMediaTemplate
            ? activeMediaTemplate.content
            : null;

        requestAnimationFrame(() => {
          activeMedia.scrollIntoView({
            behavior: 'instant',
            block: 'nearest',
            inline: 'center'
          });
        });

        if (
          activeMedia.nodeName ===
            'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector(
            '.js-youtube'
          )
        ) {
          activeMedia.loadContent();
        }

        /*
         * Prepare the neighbouring images.
         */
        setTimeout(() => {
          this.preloadAdjacentMedia(
            activeMedia
          );
        }, 300);
      }

      /*
       * CHANGE TO A DIFFERENT PRODUCT IMAGE
       */

      showMedia(media) {
        if (!media) return;

        const mediaId =
          media.getAttribute(
            'data-media-id'
          );

        this.querySelectorAll(
          '[data-media-id]'
        ).forEach((element) => {
          element.classList.toggle(
            'active',
            element.getAttribute(
              'data-media-id'
            ) === mediaId
          );
        });

        /*
         * Load this image only when
         * the customer navigates to it.
         */
        this.loadModalImage(media);

        requestAnimationFrame(() => {
          media.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        });

        const mediaTemplate =
          media.querySelector(
            'template'
          );

        const mediaContent =
          mediaTemplate
            ? mediaTemplate.content
            : null;

        if (
          media.nodeName ===
            'DEFERRED-MEDIA' &&
          mediaContent &&
          mediaContent.querySelector(
            '.js-youtube'
          )
        ) {
          media.loadContent();
        }

        /*
         * Prepare the image immediately
         * before and after the current one.
         */
        setTimeout(() => {
          this.preloadAdjacentMedia(
            media
          );
        }, 200);
      }
    }
  );
}